import React, { useState, useCallback, useRef } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { HTMLAsset, ImageAsset, useAsset } from '@techstyle/react-assets';
import { Mobile, Desktop } from '@techstyle/react-components-legacy';

import { mobile, grid } from '../../styles';
import { Component as AssetCTA } from '../AssetCTA';
import { Component as Carousel } from '../Carousel';
import { EnterViewport } from '../EnterViewport';
import { Component as TemplateImageAsset } from '../TemplateImageAsset';
import { Component as VideoCarouselItem } from '../VideoCarouselItem';

const Wrapper = styled.div`
  ${mobile`
    margin-bottom: ${props => props.marginBottomMobile}px;
  `};
`;

const Slide = styled.div`
  position: relative;
`;

const VerticalVideoWrapper = styled.div`
  position: relative;
  padding-bottom: 163%;
`;

const Container = styled.div``;

const StyledCarousel = styled(Carousel)`
  margin: 0 auto;
  max-width: 1440px;

  & .slick-track {
    display: flex;
    .slick-slide {
      float: none;
      height: auto;
    }
  }

  & .slick-slide > div {
    height: 100%;
  }

  & ul.slick-dots {
    @media (min-width: ${props => props.theme.breakpoints.xlarge}px) {
      bottom: 35px;
    }

    li button {
      width: 25px;

      &:before {
        border-color: ${props => props.theme.colors.gray250};
        box-shadow: 0 0 0 0.5px ${props => props.theme.colors.default};
        position: relative;
        margin: 0 auto;
        top: 0px;
        left: 0px;
      }
    }

    & li.slick-active button:before {
      background-color: ${props => props.theme.colors.subdued};
    }
  }
`;

const StyledImage = styled.div`
  box-sizing: content-box;
  background-image: url('${props => props.bgImage}');
  background-repeat: no-repeat;
  background-size: ${props =>
    props.backgroundSize ? props.backgroundSize : 'contain'};
  width: 100%;
  padding-bottom: 100%;
  background-position: center;

  /* Adjust additional height based on ProductCell Detail element's height and
     account for extra width added by the cell gap since we span 2 columns. */
  ${grid.small.css`
    height: ${166 - grid.small.gap}px;
    padding-bottom: 133%;
  `};

  ${grid.medium.css`
    height: ${166 - grid.medium.gap}px;
    padding-bottom: 133%;
  `};

  ${grid.large.css`
    height: ${166 - grid.large.gap}px;
    padding-bottom: 133%;
  `};

  ${grid.xlarge.css`
    height: ${166 - grid.xlarge.gap}px;
    padding-bottom: 133%;
  `};

  ${mobile`
    background-size: contain;
    padding-bottom: 115%;
    height: 5px;
  `};
`;

const Caption = styled.p`
  margin-top: 8px;
  font-size: 16px;
  font-weight: 500;

  ${mobile`
    font-size: 14px;
  `};
`;

const AssetCTAContainer = styled.div`
  margin-top: 10px;
  margin-bottom: 29px; /* Remaining space to match ProductCell Detail */
  text-align: center;
`;

const TextWrapper = styled.div`
  display: flex;
  padding: 19px;
  ${props =>
    props.isFullText
      ? `
      height: 100%;
      align-items: center;
      justify-content: center;
      text-align: center;
      `
      : `
      align-items: flex-start;
      flex-direction: column-reverse;
      text-align: left;
      max-width: 270px;
    `}

  @media screen and (min-width: ${props => props.theme.breakpoints.large}px) {
    padding-left: ${props => props.theme.spacing.medium}px;
    padding-bottom: ${props => props.theme.spacing.medium}px;
    ${props => !props.isFullText && 'max-width: 390px;'}
  }
`;

const StyledHTMLAsset = styled(HTMLAsset)`
  margin-bottom: 0;

  ${props => props.transparent && `background-color: transparent;`};

  ${grid.small.css`
    font-size: 18px;
    font-weight: bold;
    line-height: 1.17;
  `};

  /* Row of 3 cells with 16px gap */
  ${grid.medium.css`
    font-size: 24px;
    font-weight: bold;
    line-height: 1.5;
  `};

  /* Row of 4 cells with 2 x 20px gap */
  ${grid.large.css`
    font-size: 36px;
    font-weight: bold;
    line-height: 1.25;
  `};

  /* Row of 4 cells with 3 x 20px gap */
  ${grid.xlarge.css`
    font-size: 36px;
    font-weight: bold;
    line-height: 1.25;
  `};

  @media screen and (min-width: ${props => props.theme.breakpoints.xlarge}px) {
    font-size: 24px;
  }
`;

const CustomImageAsset = styled(ImageAsset)`
  box-sizing: content-box;
  padding-top: 132%;

  ${mobile`
    align-content: flex-end;
    display: flex;
  `}

  ${grid.small.css`
    height: ${170 - grid.small.gap}px;
  `};

  ${grid.medium.css`
    height: ${170 - grid.medium.gap}px;
  `};

  ${grid.large.css`
    height: ${170 - grid.large.gap}px;
  `};

  ${grid.xlarge.css`
    height: ${170 - grid.xlarge.gap}px;
  `};

  @media screen and (min-width: ${props => props.theme.breakpoints.large}px) {
    height: auto;
  }
`;

function TemplateCaptionCarousel({
  containerName,
  onAssetEnterViewport,
  videoOptions,
  videoHeight,
  ...rest
}) {
  const [swipeDate, setSwipeDate] = useState(new Date());
  const carouselRef = useRef(null);
  const carouselObserverIntersectionFired = useRef({});

  const onEnterViewportAsset = (asset, idx) => () => {
    if (!onAssetEnterViewport) {
      return;
    }

    const hasSlideEnterViewportFired =
      carouselObserverIntersectionFired.current[idx];

    if (!hasSlideEnterViewportFired) {
      carouselObserverIntersectionFired.current[idx] = true;
      onAssetEnterViewport && onAssetEnterViewport(asset);
    }
  };

  const assetContainer = useAsset(containerName, {
    multi: true,
  });
  const { data } = assetContainer;
  const assets = data ? data.assets : [];
  const customVars = assets.map(asset => asset.options.customVars);
  // if there is no autoplay in customVar, then autoplay by default
  // if there is, it should autoplay as long as there is one autoplay true in assets
  const shouldAutoPlayCarousel = customVars.some(
    customVar =>
      !Object.prototype.hasOwnProperty.call(customVar, 'autoplay') ||
      customVar.autoplay
  );

  const handleSwipe = useCallback(() => {
    setSwipeDate(new Date());
  }, [setSwipeDate]);

  if (!assets) {
    return null;
  }

  let assetCTA;

  const handlePauseCarousel = () => {
    if (carouselRef.current && shouldAutoPlayCarousel) {
      carouselRef.current.slickPause();
    }
  };

  const handlePlayCarousel = () => {
    if (carouselRef.current && shouldAutoPlayCarousel) {
      carouselRef.current.slickPlay();
    }
  };

  const assetsArray = assets.map((asset, idx) => {
    const { id, options, imageFilename, mobileImageFilename } = asset;
    const { htmlText, targetUrl } = options;
    const {
      isVertical,
      caption,
      ctaUrl,
      imagePosition,
      backgroundSize,
      videoUrl,
      isFullText,
      ctaVerticalSpace,
      ctaVerticalPosition,
      altText,
    } = asset.options.customVars;

    let content = '';

    const onClick = () => {
      if (targetUrl) {
        rest.onAssetClick(asset);
      }
    };

    if (htmlText) {
      if (imagePosition.startsWith('background')) {
        content = (
          <CustomImageAsset
            asset={asset}
            background
            bgType={imagePosition}
            onClick={onClick}
          >
            <TextWrapper isFullText={isFullText}>
              <StyledHTMLAsset transparent asset={asset} />
              <AssetCTAContainer>
                <AssetCTA {...asset} />
              </AssetCTAContainer>
            </TextWrapper>
          </CustomImageAsset>
        );
      } else {
        content = (
          <Container>
            <CustomImageAsset
              asset={asset}
              background
              maintainAspectRatio
              onClick={onClick}
            >
              <div />
            </CustomImageAsset>
            <TextWrapper isFullText={isFullText}>
              <StyledHTMLAsset asset={asset} />
              <AssetCTAContainer>
                <AssetCTA {...asset} />
              </AssetCTAContainer>
            </TextWrapper>
          </Container>
        );
      }
    } else {
      const image = imageFilename;
      const mobileImage = mobileImageFilename || image;
      content = (
        <Container onClick={onClick}>
          <Desktop>
            <StyledImage
              aria-label={altText}
              bgImage={image}
              backgroundSize={backgroundSize}
              role="img"
            />
          </Desktop>
          <Mobile>
            <StyledImage
              aria-label={altText}
              bgImage={mobileImage}
              backgroundSize={backgroundSize}
              role="img"
            />
          </Mobile>
        </Container>
      );
    }

    if (videoUrl) {
      content = (
        <VerticalVideoWrapper>
          <VideoCarouselItem
            asset={asset}
            onEnded={handlePlayCarousel}
            onPause={handlePlayCarousel}
            onPlay={handlePauseCarousel}
            swipeDate={swipeDate}
            videoHeight={videoHeight}
            videoOptions={videoOptions}
          />
        </VerticalVideoWrapper>
      );
    }

    if (isVertical && ctaVerticalPosition && ctaVerticalSpace) {
      return (
        <Slide key={id}>
          <EnterViewport
            onEnterViewport={onEnterViewportAsset(assets?.[0], idx)}
          >
            <TemplateImageAsset
              asset={asset}
              link={ctaUrl}
              isCtaOver
              onClick={onClick}
            />
          </EnterViewport>
        </Slide>
      );
    }

    if (!ctaUrl) {
      return (
        <Slide key={id}>
          <EnterViewport
            onEnterViewport={onEnterViewportAsset(assets?.[0], idx)}
          >
            {content}
          </EnterViewport>
          {caption && <Caption>{caption}</Caption>}
        </Slide>
      );
    } else {
      assetCTA = asset;
    }
  });

  return (
    <Wrapper
      marginBottomDesktop={rest.marginBottomDesktop || 64}
      marginBottomMobile={rest.marginBottomMobile || 40}
      {...rest}
    >
      <StyledCarousel
        sliderRef={carouselRef}
        options={{
          arrows: false,
          autoplay: shouldAutoPlayCarousel,
          autoplaySpeed: 5000,
          dots: true,
          infinite: true,
          onSwipe: handleSwipe,
          beforeChange: (oldIndex, newIndex) => {
            // used only for intersection observer for asset entering view
            if (!onAssetEnterViewport) {
              return;
            }

            carouselObserverIntersectionFired.current[oldIndex] = false;
            carouselObserverIntersectionFired.current[newIndex] = false;
          },
        }}
      >
        {assetsArray}
      </StyledCarousel>
      {assetCTA && (
        <AssetCTAContainer>
          <AssetCTA {...assetCTA} />
        </AssetCTAContainer>
      )}
    </Wrapper>
  );
}

TemplateCaptionCarousel.Wrapper = Wrapper;
TemplateCaptionCarousel.Caption = Caption;

TemplateCaptionCarousel.propTypes = {
  className: PropTypes.string,
  containerName: PropTypes.string.isRequired,
  onAssetClick: PropTypes.func,
  options: PropTypes.object,
  onAssetEnterViewport: PropTypes.func,
  // To add arrows to mobile carousel when slide is a youtube video
  needMobileArrows: PropTypes.bool,
  videoOptions: PropTypes.object,
  videoHeight: PropTypes.string,
};

TemplateCaptionCarousel.defaultProps = {
  containerName: null,
  options: {
    autoplay: true,
  },
  needMobileArrows: false,
  videoOptions: {
    config: {},
    controls: true,
    loop: false,
    playing: false,
  },
};

export default TemplateCaptionCarousel;
